import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, ReplaySubject, Subscription } from '../../../../node_modules/rxjs';
import { ObjectState } from '../interfaces/entity';
import { DataField, IDataFields } from '../models/data-field';
import { LookupTypeService } from './lookup-type.service';
import { SubjectType } from '../models/subject-type';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { messageService } from './messages/message.module';
import { IDataService } from './data.service';
import { KeyValuePair } from '../models/keyvaluepair';
import { Guid } from '../helpers/guid';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class DataFieldService implements IDataService, OnDestroy{

  onDataFieldsChanged: ReplaySubject<IDataFields> = new ReplaySubject(1, 100);
  onGetAvailableDataTypes: ReplaySubject<any> = new ReplaySubject(1);
  parentIsFTSUDemographicsProcessType: boolean = false;

  private onLookupTypesChanged: Subscription;

  dataFields: IDataFields;

  private lookupTypes = [];

  constructor(private lookupTypeService: LookupTypeService, protected http: HttpClient) { 
    this.onLookupTypesChanged = this.lookupTypeService.onChanged.subscribe((data) => {
      const unSortedDataTypes = [
        // MRD - Category only relates to a subject.  The subject category is a 'fixed' field, ie the user is unable to change the data type.
        // therefore we don't need to include 'Category' in this list as it should never be selectable.
        // {id: 'Category', name: 'Category'},
        { id: 'Boolean', name: 'Boolean' },
        { id: 'Number', name: 'Number' },
        { id: 'Text', name: 'Text' },
        { id: 'YesNo', name: 'YesNo' },
      ];
      this.parentIsFTSUDemographicsProcessType = !!(<any>this.dataFields?.parent)?.isFTSUDemographics
      if (!this.parentIsFTSUDemographicsProcessType) {
        unSortedDataTypes.push({ id: 'Currency', name: 'Currency' });
        unSortedDataTypes.push({ id: 'Date', name: 'Date' }),
        unSortedDataTypes.push({ id: 'Memo', name: 'Memo' });
        unSortedDataTypes.push({ id: 'User', name: 'User' });
        unSortedDataTypes.push({ id: 'Calculated', name: 'Calculated' });
      }
      
      // MRD - include status if parent is a subject and a data field of type 'status' has not already been added
      if (
        this.dataFields &&
        this.dataFields.parent &&
        this.dataFields.parent instanceof SubjectType &&
        (!this.dataFields.dataFields ||
          this.dataFields.dataFields.findIndex((x) => x.dataType.toLowerCase() === 'status') < 0) &&
        !this.parentIsFTSUDemographicsProcessType
      ) {
        unSortedDataTypes.push({ id: 'Status', name: 'Status' });
      }
      
      if (data.length > 0){
        const lookupTypes = data;
        this.lookupTypes = lookupTypes;
        lookupTypes.forEach((lookupType) => {
          if (!lookupType.hideRecord && (!this.parentIsFTSUDemographicsProcessType || lookupType.isFTSUDemographics)) {
            unSortedDataTypes.push({id: lookupType.id.toString(), name: lookupType.description});
          }
        });
        const sortedDataTypes = unSortedDataTypes.sort((n1, n2) => {
          if (n1 > n2){
            return 1;
          }
          if (n1 < n2){
            return -1;
          }
          return 0;
        });
        this.onGetAvailableDataTypes.next(sortedDataTypes);
      }
      else{
        this.onGetAvailableDataTypes.next(unSortedDataTypes);
      }
    });
  }

  ngOnDestroy(){
    this.onLookupTypesChanged?.unsubscribe();
  }

  // functions for handling of changes to dataFields
  save(field: DataField){
    if (field && field.name){
      if (field.id){
        this.editField(field);
      }
      else{
        this.addField(field);
      }
    }
  }

  delete(id: number, parentTypeID?: string){
    const dataField = this.dataFields.dataFields.find((x) => x.id === id);
    const index = this.dataFields.dataFields.findIndex((x) => x.id === id);
    if ((!parentTypeID) || (dataField.objectState === ObjectState.Added)){
      if (index > -1){
        this.dataFields.dataFields.splice(index, 1);
      }
      this.onDataFieldsChanged.next(this.dataFields);
    }
    else{
      const httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      };
      
      if (dataField){
        const prefix = ((this.dataFields.parent as SubjectType).primary !== undefined) ? 'subject' : 'process';
        // need to do a "can delete" here
        const url = '/api/' + prefix + 'Type/' + parentTypeID + '/metadata/canDeleteDataField/' + dataField.uid;
        
        // check to see if we can delete the field or not. will return an empty string or an error message
        this.http.get<string>(url, httpOptions)
        .subscribe((data) => {

          if (data === '')
          {
            dataField.objectState = ObjectState.Deleted;
            this.dataFields.dataFields.splice(index , 1);
            this.onDataFieldsChanged.next(this.dataFields);
          }
          else
          {
            messageService.error('Delete failed. ' + data);
          }
          });
        }
      }
  }

  private addField(field: DataField){
    // MRD - add id manually
    if (this.dataFields.dataFields.length > 0){
      field.id = this.dataFields.dataFields.reduce((max, e) => e.id > max ? e.id : max, this.dataFields.dataFields[0].id) + 1;
    }
    else{
      field.id = 1;
    }
    field.uid = Guid.newGuid();
    this.hydrate(field);
    field.objectState = ObjectState.Added;
    this.dataFields.dataFields.push(field);
    this.onDataFieldsChanged.next(this.dataFields);
  }

  private editField(field: DataField){
    const index = this.dataFields.dataFields.findIndex((x) => x.id === field.id);
    if (index > -1){
      this.hydrate(field);
      // if the field has been already saved then object state will have been reset
      // set to modified.
      if (field.objectState !== ObjectState.Added){
        field.objectState = ObjectState.Modified;
      }
      this.dataFields.dataFields[index] = field;
      this.onDataFieldsChanged.next(this.dataFields);
    }
  }

  getNumericFields(){
    if (this.dataFields) {
      const validTypes = ['number', 'currency', 'calculated'];
      return this.dataFields.dataFields.filter((x) => validTypes.includes(x.dataType.toLowerCase())).sort((a, b) => a.label.localeCompare(b.label));
    }
    else{
      return [];
    }
  }

  getAvailableDataTypes(){
    this.lookupTypeService.getAll();
  }

  hydrate(item: DataField){
    if (item.dataType){
      if (/^\d+$/.test(item.dataType)){
        const lookupType = this.lookupTypes.find((x) => x.id.toString() === item.dataType);
        if (lookupType){
          item.dataTypeName = lookupType.description;
        }
      }
      else{
        item.dataTypeName = item.dataType;
      }
    }
  }

  isUnique(val: string, id: number|string, propertyName: string): Observable<boolean>{
    if (val){
      const item = this.dataFields.dataFields.find((i) => (i[propertyName]) && (i.id !== id) 
        && (i[propertyName].toString().toLowerCase().replace(/\s/g, '') === val.toString().toLowerCase().replace(/\s/g, '')));
      return of(item ? false : true);
    }
    else{
      return of(true);
    }
  }
  
  isUniqueCombination(id: number|string, properties: KeyValuePair[]): Observable<boolean>{
    if (properties.length > 0){
      let matchingRecords = this.dataFields.dataFields;
      properties.forEach((r) => {
        const val = r.value.toString().toLowerCase().replace(/\s/g, '');
        matchingRecords = matchingRecords.filter((i) => (i[r.key]) && (i.id !== id) 
        && (i[r.key].toString().toLowerCase().replace(/\s/g, '') === val));
      });
      return of((matchingRecords.length > 0) ? false : true);
    }
    else{
      return of(true);
    }
  }

}
